import React, { useState, useEffect, useCallback } from 'react';
import { api } from './api';
import Icon from './Icon';

// A single drag-and-drop deploy zone.
const DropZone = ({ title, icon, desc, accentClass, disabled, disabledReason, confirmMsg, onDeploy }) => {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFile = async (file) => {
    setError(''); setResult(null);
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) { setError('Please drop a .zip file.'); return; }
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const data = await onDeploy(file);
      setResult(data.message || 'Done.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`deploy-card ${accentClass}`}>
      <h3>{icon && <Icon name={icon} size={18} />} {title}</h3>
      <p className="admin-subtle">{desc}</p>

      <label
        className={`dropzone ${drag ? 'dragover' : ''} ${disabled ? 'disabled' : ''}`}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (!disabled) handleFile(e.dataTransfer.files[0]); }}
      >
        <input
          type="file"
          accept=".zip"
          hidden
          disabled={disabled || busy}
          onChange={(e) => handleFile(e.target.files[0])}
        />
        {busy ? (
          <div className="dz-inner"><div className="spinner" /><span>Deploying…</span></div>
        ) : (
          <div className="dz-inner">
            <span className="dz-icon"><Icon name="upload" size={28} /></span>
            <span><strong>Drop your .zip here</strong> or click to browse</span>
          </div>
        )}
      </label>

      {disabled && <p className="admin-error"><Icon name="alert-triangle" size={15} /> {disabledReason}</p>}
      {error && <p className="admin-error"><Icon name="alert" size={15} /> {error}</p>}
      {result && <p className="deploy-ok">{result}</p>}
    </div>
  );
};

// Editable folder-path settings, saved server-side (no .env editing needed).
const PathSettings = ({ info, onSaved }) => {
  const [fe, setFe] = useState('');
  const [be, setBe] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (info) { setFe(info.frontend.path || ''); setBe(info.backend.path || ''); }
  }, [info]);

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const updated = await api.saveDeployConfig({ frontendPath: fe, backendPath: be });
      onSaved(updated);
      setMsg('Saved');
      setTimeout(() => setMsg(''), 2000);
    } catch (err) { setMsg('Error: ' + err.message); }
    finally { setBusy(false); }
  };

  const dot = (t) => (info?.[t]?.writable ? 'ok' : info?.[t]?.exists ? 'warn-dot' : 'bad');
  const ftpMode = info?.ftp?.configured;

  return (
    <div className="deploy-config">
      <h4>Folder paths</h4>
      <p className="admin-subtle" style={{ margin: '0 0 14px' }}>
        {ftpMode
          ? <>The website deploys over <strong>FTP</strong> (configured below), so no local website folder is needed. This <strong>Server folder</strong> is only where the backend code is written on this host.</>
          : <>Where the tool writes files. Set the <strong>Website folder</strong> to your site’s document root (cPanel → Domains → Document Root). The app must have write access to it.</>}
      </p>

      {!ftpMode && (
        <label className="path-field">
          <span><span className={`light ${dot('frontend')}`} /> Website folder</span>
          <input value={fe} onChange={(e) => setFe(e.target.value)} placeholder="/home/USER/public_html" spellCheck={false} />
        </label>
      )}
      <label className="path-field">
        <span><span className={`light ${dot('backend')}`} /> Server folder</span>
        <input value={be} onChange={(e) => setBe(e.target.value)} placeholder="/home/USER/.../backend" spellCheck={false} />
      </label>

      <div className="path-actions">
        <button className="admin-primary-btn" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save paths'}</button>
        {msg && <span className="path-msg">{msg}</span>}
      </div>

      <ul className="path-status">
        <li><span className={`light ${info?.unzip ? 'ok' : 'bad'}`} /> unzip tool: {info?.unzip ? 'available' : 'missing'}</li>
        {ftpMode
          ? <li><span className="light ok" /> Website: via FTP</li>
          : <li><span className={`light ${dot('frontend')}`} /> Website: {info?.frontend?.writable ? 'writable' : info?.frontend?.exists ? 'exists but not writable' : 'folder not found'}</li>}
        <li><span className={`light ${dot('backend')}`} /> Server: {info?.backend?.writable ? 'writable' : info?.backend?.exists ? 'exists but not writable' : 'folder not found'}</li>
      </ul>
    </div>
  );
};

// Website FTP/FTPS settings — for when the site is on a different host.
const FtpSettings = ({ info, onSaved }) => {
  const [f, setF] = useState({ host: '', port: 21, user: '', password: '', secure: false, remotePath: '' });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (info?.ftp) setF((p) => ({ ...p, host: info.ftp.host, port: info.ftp.port, user: info.ftp.user, secure: info.ftp.secure, remotePath: info.ftp.remotePath, password: '' }));
  }, [info]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const save = async () => {
    setBusy(true); setMsg('');
    try { onSaved(await api.saveFtp(f)); setMsg('Saved'); setTimeout(() => setMsg(''), 2000); }
    catch (err) { setMsg('Error: ' + err.message); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true); setMsg('');
    try { const r = await api.testFtp(); setMsg(r.message); }
    catch (err) { setMsg('Error: ' + err.message); }
    finally { setTesting(false); }
  };

  return (
    <div className="deploy-config">
      <h4>Website FTP / FTPS {info?.ftp?.configured && <span className="ftp-on">● active</span>}</h4>
      <p className="admin-subtle" style={{ margin: '0 0 14px' }}>
        Use this when your website lives on a <strong>different host</strong> than this server. The build is
        pushed to the remote host over FTP. {info?.ftp?.hasPassword && !f.password && <em>(password saved — leave blank to keep)</em>}
      </p>
      <div className="ftp-grid">
        <label className="path-field" style={{ gridColumn: 'span 2' }}><span>FTP host</span><input value={f.host} onChange={set('host')} placeholder="ftp.jgdetailingllc.com" spellCheck={false} /></label>
        <label className="path-field"><span>Port</span><input value={f.port} onChange={set('port')} placeholder="21" /></label>
        <label className="path-field"><span>Secure (FTPS)</span><span className="ftp-check"><input type="checkbox" checked={f.secure} onChange={set('secure')} /> use FTPS</span></label>
        <label className="path-field"><span>Username</span><input value={f.user} onChange={set('user')} spellCheck={false} /></label>
        <label className="path-field"><span>Password</span><input type="password" value={f.password} onChange={set('password')} placeholder={info?.ftp?.hasPassword ? '•••••• (saved)' : ''} /></label>
        <label className="path-field" style={{ gridColumn: 'span 2' }}><span>Remote folder (document root)</span><input value={f.remotePath} onChange={set('remotePath')} placeholder="/public_html" spellCheck={false} /></label>
      </div>
      <div className="path-actions">
        <button className="admin-primary-btn" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save FTP settings'}</button>
        <button className="admin-ghost-btn" onClick={test} disabled={testing || !info?.ftp?.configured}>{testing ? 'Testing…' : 'Test connection'}</button>
        {msg && <span className="path-msg">{msg}</span>}
      </div>
    </div>
  );
};

const DeployPanel = () => {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try { setInfo(await api.deployInfo()); setError(''); }
    catch (err) { setError(err.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ftpMode = info?.ftp?.configured;
  const feReady = info?.unzip && (ftpMode || info?.frontend?.writable);
  const beReady = info?.unzip && info?.backend?.writable;

  const reason = (target) => {
    if (!info) return 'Loading…';
    if (!info.unzip) return 'The server is missing the `unzip` tool. Contact your host.';
    if (target === 'frontend') {
      if (ftpMode) return '';
      return 'Set the Website folder below, or configure FTP if the site is on another host.';
    }
    if (!info.backend.writable) return `Server folder not writable: ${info.backend.path}`;
    return '';
  };

  return (
    <div className="deploy-panel">
      <div className="deploy-note">
        <Icon name="lock" size={15} /> Deploys replace website/server files only. <strong>Your database and bookings are never touched.</strong>
      </div>

      {error && <p className="admin-error"><Icon name="alert" size={15} /> {error}</p>}

      <div className="deploy-grid">
        <DropZone
          title="Update Website"
          icon="globe"
          desc={ftpMode
            ? 'Drop the website zip — it’s pushed to your website host over FTP. No restart needed.'
            : 'Drop the website zip. Goes live instantly — no restart needed.'}
          accentClass="fe"
          disabled={!feReady}
          disabledReason={reason('frontend')}
          onDeploy={api.deployFrontend}
        />
        <DropZone
          title="Update Server"
          icon="settings"
          desc="Drop the server code zip (server-update.zip). The app restarts automatically after deploy."
          accentClass="be"
          disabled={!beReady}
          disabledReason={reason('backend')}
          confirmMsg="Deploy new server code and restart the app? The site API will blink for a few seconds."
          onDeploy={api.deployBackend}
        />
      </div>

      {info && <PathSettings info={info} onSaved={setInfo} />}
      {info && <FtpSettings info={info} onSaved={setInfo} />}
    </div>
  );
};

export default DeployPanel;
